import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface ActivityLog {
  id: string;
  userId: string;
  userName: string;
  action: 'login' | 'logout' | 'signup' | 'settings_change' | 'content_creation' | 'plan_generation';
  details: string;
  timestamp: string;
  metadata?: {
    userAgent?: string;
    timestamp?: string;
    userDetails?: Record<string, any>;
    [key: string]: any;
  };
}

interface ActivityLogStore {
  logs: ActivityLog[];
  addLog: (log: Omit<ActivityLog, 'id' | 'timestamp'>) => void;
  clearLogs: () => void;
  getLogsByUser: (userId: string) => ActivityLog[];
  getLogsByAction: (action: ActivityLog['action']) => ActivityLog[];
  getRecentLogs: (count: number) => ActivityLog[];
  getLogsBetweenDates: (startDate: string, endDate: string) => ActivityLog[];
  getTotalLogsCount: () => number;
  getActiveUsersCount: () => number;
}

export const useActivityLogStore = create<ActivityLogStore>()(
  persist(
    (set, get) => ({
      logs: [],
      addLog: (log) => set((state) => ({
        logs: [{
          ...log,
          id: crypto.randomUUID(),
          timestamp: new Date().toISOString(),
        }, ...state.logs]
      })),
      clearLogs: () => set({ logs: [] }),
      getLogsByUser: (userId) => get().logs.filter(log => log.userId === userId),
      getLogsByAction: (action) => get().logs.filter(log => log.action === action),
      getRecentLogs: (count) => get().logs.slice(0, count),
      getLogsBetweenDates: (startDate, endDate) => {
        const start = new Date(startDate).getTime();
        const end = new Date(endDate).getTime();
        return get().logs.filter(log => {
          const logTime = new Date(log.timestamp).getTime();
          return logTime >= start && logTime <= end;
        });
      },
      getTotalLogsCount: () => get().logs.length,
      getActiveUsersCount: () => {
        const uniqueUsers = new Set(get().logs.map(log => log.userId));
        return uniqueUsers.size;
      }
    }),
    {
      name: 'activity-logs',
      version: 1,
      migrate: (persistedState: any, version: number) => persistedState
    }
  )
);